import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans } from 'react-i18next';

import { UploadPrefix } from 'entities';

import { Column, Row } from 'components/Blocks';
import { FolderSearchIcon } from 'components/Icons';

import SingleFile from './File';

import { UploadedFile } from 'types';
import { getID } from 'utils/id';
import { cn } from 'styles';
import { FileWithId } from '../types';
import { useLang } from 'hooks/lang';

interface FileDropzoneProps {
  files: UploadedFile[];
  setFiles: Dispatch<SetStateAction<UploadedFile[]>>;
  uploadPrefix: UploadPrefix;
  acceptedFormats?: string[];
  multiple?: boolean;
  showInputOnDisabled?: boolean;
  onLoading?: (loading: boolean) => void;
  className?: string;
}

export function UploadFileDropzone({
  files,
  setFiles,
  uploadPrefix,
  acceptedFormats,
  multiple = true,
  showInputOnDisabled = true,
  onLoading,
  className,
}: FileDropzoneProps) {
  const { t } = useLang();

  const [filesToUpload, setFilesToUpload] = useState<FileWithId[]>([]);

  const removeFile = (toRemoveId: string): void => {
    setFiles((old) => old.filter(({ id }) => id !== toRemoveId));
    setFilesToUpload((old) => old.filter(({ id }) => id !== toRemoveId));
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!multiple && files.length === 1) return;

      onLoading?.(true);

      setFilesToUpload((old) => [
        ...old,
        ...acceptedFiles.map((file) => {
          (file as FileWithId).id = getID();

          return file as FileWithId;
        }),
      ]);
    },
    [multiple, files.length, onLoading],
  );

  const allFiles = [...files, ...filesToUpload];

  const disabled = multiple ? false : allFiles.length === 1;

  const onUpload = ({ file, uploaded }: { file: FileWithId; uploaded: UploadedFile }): void => {
    setFiles((old) => [...old, uploaded]);

    setFilesToUpload((old) => {
      const left = old.filter((oldFile) => oldFile.id !== file.id);

      setTimeout(() => onLoading?.(!!left.length), 0);

      return left;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept: acceptedFormats,
    disabled,
  });

  const displayInput = disabled ? showInputOnDisabled : true;

  return (
    <Column className={cn('h-auto select-none', className)} gap="2">
      {displayInput && (
        <div className="flex justify-center rounded-md border p-3" {...getRootProps()}>
          <input {...getInputProps()} />

          {isDragActive ? (
            <span className="text-sm font-medium">{t('drop-file-here')}</span>
          ) : (
            <Column alignItems="center" gap="1">
              <span className="text-center text-sm font-medium">
                <Trans>drag-and-drop-files-or</Trans>
              </span>

              <Row
                gap="2"
                justifyContent="center"
                className="h-6 w-[150px] rounded-lg bg-primary text-sm font-semibold text-primary-foreground"
              >
                <FolderSearchIcon size={16} />
                {t('browse')}
              </Row>
            </Column>
          )}
        </div>
      )}

      <Row className="scrollbar overflow-x-auto pb-2" gap="2">
        {allFiles.map((file) => (
          <SingleFile
            key={`file-single-${file.id}`}
            handleDelete={() => removeFile((file as UploadedFile).id)}
            file={file}
            onUpload={onUpload}
            prefix={uploadPrefix}
          />
        ))}
      </Row>
    </Column>
  );
}
